<template>
  <b-card :class="[bgColor]" class="border-none">
    <validation-observer ref="form">
      <b-row>
        <b-col cols="12" class="mb-3">
          <span class="border rounded-circle shadow-sm p-1 mr-2">
            <i class="fas fa-edit text-orange" />
          </span>
          <span class="font-weight-bold text-gray-dark">
            Editar Cupo de Estudiantes
          </span>
        </b-col>
        <b-col cols="12" md>
          <div class="color-secondary-6 font-14">
            Límite de Cupo de Estudiantes:
          </div>
          <div class="color-black font-18">
            {{ studentLimit | empty }}
          </div>
        </b-col>
        <b-col cols="12" md>
          <validation-provider
            v-slot="{ errors }"
            :rules="validations.rules"
            :custom-messages="validations.messages"
          >
            <custom-input
              type="number"
              v-model="model.studentCapacity"
              label="Cambio de Cupo de Estudiantes:"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </b-col>
        <b-col cols="12" md>
          <validation-provider
            v-slot="{ errors }"
            rules="required"
            :custom-messages="{
              required: 'La razón es requerida.',
            }"
          >
            <custom-drop-down
              v-model="model.reason"
              regular-label
              clearable
              label="Razón:"
              placeholder="Seleccione"
              fieldkey="id"
              fieldname="name"
              :options="reasons"
              :error="errors[0]"
              :custom-error-msg="errors[0]"
            />
          </validation-provider>
        </b-col>
        <b-col cols="12">
          <ValidationProvider
            v-slot="{ errors }"
            name="comment"
            rules="required"
          >
            <custom-text-area
              v-model="model.comment"
              required
              :maxlength="3000"
              label="Comentario:"
              :error="errors[0]"
              :custom-error-msg="'Campo requerido'"
              show-char-count
              :count-classes="['d-block', 'small-counter', 'text-right']"
            />
          </ValidationProvider>
        </b-col>
        <b-col cols="12" class="my-3">
          <warning-box
            class="mx-0"
            title="El cupo debe ser menor o igual al cupo límite computado."
          />
        </b-col>
        <b-col cols="12">
          <div class="d-flex flex-column flex-sm-row justify-content-end">
            <div>
              <action-button
                class="btn-block"
                text="Cancelar"
                variant="outline-danger"
                i="fas fa-times-circle"
                @click="cancel"
              />
            </div>
            <div class="my-2 mx-2 my-sm-0" />
            <div>
              <action-button
                class="btn-block"
                text="Guardar"
                variant="success"
                i="fas fa-save"
                @click="save"
              />
            </div>
          </div>
        </b-col>
      </b-row>
    </validation-observer>
  </b-card>
</template>
<script>
import { ValidationObserver, ValidationProvider } from 'vee-validate';
import CustomDropDown from 'custom-drop-down';
import CustomInput from 'CustomInput';
import ActionButton from 'ActionButton';
import WarningBox from '@/components/pei/service/components/ServiceWarningBox';
import alert from '@/mixin/alert.mixin';
import * as api from '@/views/pei/location-alternatives/_api';
import CustomTextArea from 'CustomTextArea';

export default {
  name: 'LocationAlternativeEditStudentCapacity',
  mixins: [alert],
  components: {
    ValidationObserver,
    ValidationProvider,
    CustomDropDown,
    CustomInput,
    ActionButton,
    WarningBox,
    CustomTextArea,
  },
  props: {
    bgColor: {
      type: String,
      required: false,
      default: () => 'bg-white',
    },
    limit: {
      type: Number,
      required: false,
      default: () => 0,
    },
    capacity: {
      type: Number,
      required: false,
      default: () => 0,
    },
    locationId: {
      type: Number,
      required: true,
      default: () => null,
    },
  },
  data: () => ({
    model: {
      studentCapacity: null,
      reason: null,
      comment: null,
    },
    studentLimit: null,
    reasons: [],
  }),
  async mounted() {
    await this.init();
  },
  computed: {
    validations() {
      const rules = ['required', 'min_value:1'];
      const messages = {
        required: 'El cupo de estudiantes es requerido.',
        min_value: 'El cupo de estudiantes debe ser mayor a 0.',
      };
      if (this.studentLimit) {
        rules.push(`max_value:${this.studentLimit}`);
        messages.max_value = `El cupo de estudiantes debe ser menor o igual a ${this.studentLimit}.`;
      }
      return {
        rules: rules.join('|'),
        messages,
      };
    },
  },
  methods: {
    async init() {
      await Promise.all([
        this.getCapacity(),
        this.getCapacityLimit(),
        this.getReasons(),
      ]);
    },
    async getCapacity() {
      let capacity = this.capacity;
      if (!capacity) capacity = await api.getStudentCapacity(this.locationId);
      this.model.studentCapacity = capacity;
    },
    async getCapacityLimit() {
      let limit = this.limit;
      if (!limit) limit = await api.getStudentCapacityLimit(this.locationId);
      this.studentLimit = limit;
    },
    async save() {
      const valid = await this.$refs['form'].validate();
      if (!valid) {
        this.ShowToast(
          '',
          'Favor de completar información para continuar',
          'error'
        );
        return;
      }
      const payload = {
        capacity: Number(this.model.studentCapacity),
        reasonId: this.model.reason?.id,
        comment: this.model.comment,
      };
      try {
        await api.updateStudentCapacity(this.locationId, payload);
        this.ShowToast(
          '',
          'El Cupo de Estudiantes ha sido guardado exitosamente.',
          'success'
        );
        this.$emit('success', payload.capacity);
      } catch (err) {
        this.ShowToast('', err?.response?.data?.message, 'error');
      }
    },
    cancel() {
      this.model = { studentCapacity: null, reason: null };
      this.$refs['form'].reset();
      this.$emit('cancel');
    },
    async getReasons() {
      this.reasons = await api.getStudentCapacityChangeReasons();
      if (this.reasons?.length === 1) this.model.reason = this.reasons[0];
    },
  },
};
</script>
